import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

// material
import {
  Box,
  Divider,
  Typography,
  Stack,
  FormGroup,
  FormControlLabel,
  Switch,
  Avatar,
  MenuItem
} from '@mui/material';
// components
import { useDispatch, useSelector } from 'react-redux';

import MenuPopover from '../../components/MenuPopover';
import { map } from '../Map';
import { styleMapboxOutdoors, styleMapboxSatelliteStreets } from '../mapStyles';
import { aerosActions, sessionActions } from '../../store';
import { editItem } from '../../fetch';
//

import SatelliteImage from '../../resources/images/satellite-image-earth.jpg';
import OutdoorsImage from '../../resources/images/outdoors-image.jpg';
import { useEffectAsync } from '../../reactHelper';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectSession, selectShowAllAeros } from '../../createSelectorUtils';

// ----------------------------------------------------------------------
SwitcherMenu.propTypes = {
  switcher: PropTypes.any
};
export default function SwitcherMenu({ switcher }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const anchorRef = useRef(document.querySelector('.mapboxgl-style-switcher'));
  const sharePage = window.location.pathname === '/vol/share';
  const session = useSelector(selectSession);
  const showAllAero = useSelector(selectShowAllAeros);

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  useEffectAsync(async () => {
    const handleOpen = () => {
      setOpen(true);
    };

    if (anchorRef.current) {
      anchorRef.current.addEventListener('click', await handleOpen);
    }

    return () => {
      anchorRef.current.removeEventListener('click', handleOpen);
    };
  }, [anchorRef]);

  const handleChangeSession = async (body) => {
    if (!sharePage) {
      delete body.permission;
      const response = await editItem('users', body.id, body);
      if (response.ok) {
        dispatch(sessionActions.update(await response.json()));
      }
    }
  };

  const handleChangleMapStyle = (style) => {
    if (style === 'mapboxOutdoors') {
      switcher.onSelectStyle({ uri: styleMapboxOutdoors() });
      handleChangeSession({ ...session, map: 'Mapbox Outdoors' });
    } else {
      switcher.onSelectStyle({ uri: styleMapboxSatelliteStreets() });
      handleChangeSession({ ...session, map: 'Mapbox Satellite' });
    }
  };

  const handleChangeOaci = () => {
    if (map.getSource('icao') && map.getLayer('icao-layer')) {
      if (map.getLayoutProperty('icao-layer', 'visibility') === 'visible') {
        map.setLayoutProperty('icao-layer', 'visibility', 'none');
        handleChangeSession({
          ...session,
          attributes: { ...session.attributes, icaoView: false }
        });
      } else if (map.getLayoutProperty('icao-layer', 'visibility') === 'none') {
        map.setLayoutProperty('icao-layer', 'visibility', 'visible');
        map.easeTo({ zoom: 10.8 });
        handleChangeSession({
          ...session,
          attributes: { ...session.attributes, icaoView: true }
        });
      }
    }
  };

  const handleChangeZoneP = (event) => {
    if (map.getSource('geofences_P')) {
      if (
        map.getLayoutProperty('geofences-fill-P', 'visibility') === 'visible' &&
        !event.target.checked
      ) {
        map.setLayoutProperty('geofences-fill-P', 'visibility', 'none');
        map.setLayoutProperty('geofences-line-P', 'visibility', 'none');
        map.setLayoutProperty('geofences-title-P', 'visibility', 'none');
      } else if (
        map.getLayoutProperty('geofences-fill-P', 'visibility') === 'none' &&
        event.target.checked
      ) {
        map.setLayoutProperty('geofences-fill-P', 'visibility', 'visible');
        map.setLayoutProperty('geofences-line-P', 'visibility', 'visible');
        map.setLayoutProperty('geofences-title-P', 'visibility', 'visible');
      }

      handleChangeSession({
        ...session,
        attributes: {
          ...session.attributes,
          geofencesP: event.target.checked
        }
      });
    }
  };
  const handleChangeZoneA = (event) => {
    if (map.getSource('geofences_A')) {
      if (
        map.getLayoutProperty('geofences-fill-A', 'visibility') === 'visible' &&
        !event.target.checked
      ) {
        map.setLayoutProperty('geofences-fill-A', 'visibility', 'none');
        map.setLayoutProperty('geofences-line-A', 'visibility', 'none');
        map.setLayoutProperty('geofences-title-A', 'visibility', 'none');
      } else if (
        map.getLayoutProperty('geofences-fill-A', 'visibility') === 'none' &&
        event.target.checked
      ) {
        map.setLayoutProperty('geofences-fill-A', 'visibility', 'visible');
        map.setLayoutProperty('geofences-line-A', 'visibility', 'visible');
        map.setLayoutProperty('geofences-title-A', 'visibility', 'visible');
      }
    }
    handleChangeSession({
      ...session,
      attributes: { ...session.attributes, geofencesA: event.target.checked }
    });
  };
  const handleChangeZoneVac = (event) => {
    if (map.getSource('geofences_Vac')) {
      if (
        map.getLayoutProperty('geofences-fill-Vac', 'visibility') === 'visible' &&
        !event.target.checked
      ) {
        map.setLayoutProperty('geofences-fill-Vac', 'visibility', 'none');
        map.setLayoutProperty('geofences-line-Vac', 'visibility', 'none');
        map.setLayoutProperty('geofences-title-Vac', 'visibility', 'none');
      } else if (
        map.getLayoutProperty('geofences-fill-Vac', 'visibility') === 'none' &&
        event.target.checked
      ) {
        map.setLayoutProperty('geofences-fill-Vac', 'visibility', 'visible');
        map.setLayoutProperty('geofences-line-Vac', 'visibility', 'visible');
        map.setLayoutProperty('geofences-title-Vac', 'visibility', 'visible');
      }
    }
    handleChangeSession({
      ...session,
      attributes: {
        ...session.attributes,
        geofencesVac: event.target.checked
      }
    });
  };

  const handleAllAero = (event) => {
    dispatch(aerosActions.updateShowAll(event.target.checked));
  };

  return (
    <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 250 }}>
      <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, mt: 1 }}>
        Map
      </Typography>
      <MenuItem
        onClick={() => handleChangleMapStyle('mapboxSatellite')}
        selected={map.getStyle() && map.getStyle().name === 'Mapbox Satellite Streets'}
      >
        <Stack spacing={3} direction="row" alignItems="center">
          <Avatar variant="rounded" src={SatelliteImage} sx={{ width: 100, height: 100 }} />
          <Typography variant="body1" color="black">
            Satellite
          </Typography>
        </Stack>
      </MenuItem>
      <MenuItem
        onClick={() => handleChangleMapStyle('mapboxOutdoors')}
        selected={map.getStyle() && map.getStyle().name === 'Mapbox Outdoors'}
      >
        <Stack spacing={3} direction="row" alignItems="center">
          <Avatar variant="rounded" src={OutdoorsImage} sx={{ width: 100, height: 100 }} />
          <Typography variant="body1" color="black">
            Outdoors
          </Typography>
        </Stack>
      </MenuItem>
      <Divider sx={{ my: 1 }} />

      <Box>
        <FormGroup sx={{ ml: 3 }}>
          {!sharePage && window.location.pathname !== '/geofences' && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleChangeZoneP}
                    checked={session.attributes.geofencesP || false}
                  />
                }
                label={t('area_P')}
              />
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleChangeZoneA}
                    checked={session.attributes.geofencesA || false}
                  />
                }
                label={t('Class_A')}
              />
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleChangeZoneVac}
                    checked={session.attributes.geofencesVac || false}
                  />
                }
                label={t('area_Vac')}
              />
            </>
          )}

          {(window.location.pathname === '/report/club' ||
            window.location.pathname === '/dashboard') && (
            <FormControlLabel
              control={<Switch onChange={handleAllAero} checked={showAllAero} />}
              label={t('aerosList')}
            />
          )}

          <FormControlLabel
            control={
              <Switch
                onChange={handleChangeOaci}
                checked={
                  (session.attributes && session.attributes.icaoView) ||
                  (map.getLayer('icao-layer') &&
                    map.getLayoutProperty('icao-layer', 'visibility') === 'visible') ||
                  false
                }
              />
            }
            label={t('Icao_FR')}
          />
        </FormGroup>
      </Box>
    </MenuPopover>
  );
}
